import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchTopEpisodes, slugify, pageData, episodeMetric } from '../api.js';
import { applyMeta, leaderboardMeta } from '../seo.js';

function formatMs(ms) {
  if (ms == null) return '';
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatPct(val) {
  if (val == null) return '';
  return `${val.toFixed(1)}%`;
}

const notSolo = (ep) => !ep.solo_episode;

const SECTIONS = [
  {
    key: 'host_pct',
    label: 'Most host-dominated',
    icon: '🎙',
    format: formatPct,
    statLabel: 'Host %',
    // Exclude: solo episodes and Crime Junkie (narrative format structurally inflates host%)
    filter: (ep) =>
      notSolo(ep) &&
      (ep.guest_pct ?? 0) >= 10 &&
      ep.podcast_uuid !== 'crime-junkie',
  },
  {
    key: 'guest_pct',
    label: 'Most guest-dominated',
    icon: '🎤',
    format: formatPct,
    statLabel: 'Guest %',
    filter: notSolo,
  },
  {
    key: 'turn_switches',
    label: 'Most back-and-forth',
    icon: '💬',
    format: (v) => v != null ? v.toLocaleString() : '',
    statLabel: 'Turn switches',
    filter: notSolo,
  },
  {
    key: 'longest_monologue_ms',
    label: 'Longest monologue',
    icon: '⏱',
    format: formatMs,
    statLabel: 'Duration',
    filter: notSolo,
  },
  {
    key: 'host_questions',
    label: 'Most host questions',
    icon: '❓',
    format: (v) => v != null ? v.toLocaleString() : '',
    // Questions asked by everyone hosting the episode, however many that is.
    // api.js's episodeMetric works that out, because the raw `host_questions`
    // field holds only one of the two hosts on a hosts-only pair.
    statLabel: 'Host questions',
    filter: notSolo,
  },
];

function LeaderboardSection({ section, seed }) {
  const [episodes, setEpisodes] = useState(seed ?? null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (seed) return;
    let cancelled = false;
    fetchTopEpisodes(section.key, 10, section.filter)
      .then(eps => { if (!cancelled) setEpisodes(eps); })
      .catch(err => { if (!cancelled) { console.error('Failed to load leaderboard section:', err); setError(true); } });
    return () => { cancelled = true; };
  }, [section.key, seed]);

  return (
    <section className="leaderboard-section" aria-label={section.label}>
      <h2 className="leaderboard-section__heading">
        <span aria-hidden="true">{section.icon}</span>
        {section.label}
      </h2>

      {error && (
        <p style={{ color: 'var(--color-danger)', fontSize: '0.82rem' }}>
          Failed to load leaderboard. Please try again.
        </p>
      )}

      {!error && episodes === null && (
        <table className="leaderboard-table" aria-busy="true" aria-label={`${section.label} loading`}>
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Episode</th>
              <th scope="col" style={{ textAlign: 'right' }}>{section.statLabel}</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }, (_, i) => (
              <tr key={i} aria-hidden="true">
                <td><div className="skeleton" style={{ height: 12, width: 20, borderRadius: 4 }} /></td>
                <td>
                  <div className="skeleton" style={{ height: 12, width: '70%', borderRadius: 4 }} />
                  <div className="skeleton" style={{ height: 10, width: '40%', borderRadius: 4, marginTop: 4 }} />
                </td>
                <td><div className="skeleton" style={{ height: 12, width: 50, borderRadius: 4, marginLeft: 'auto' }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!error && episodes !== null && episodes.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>
          No data available yet.
        </p>
      )}

      {!error && episodes !== null && episodes.length > 0 && (
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Episode</th>
              <th scope="col" style={{ textAlign: 'right' }}>{section.statLabel}</th>
            </tr>
          </thead>
          <tbody>
            {episodes.map((ep, i) => {
              // Link to episode detail page using clean slug URL
              const dest = ep.podcast_uuid && ep.title
                ? `/podcast/${ep.podcast_uuid}/episode/${slugify(ep.title)}`
                : ep.podcast_uuid
                  ? `/podcast/${ep.podcast_uuid}`
                  : '/';
              return (
                <tr key={ep.episode_uuid ?? `${ep.podcast_uuid}-${i}`}>
                  <td>
                    <span className={`leaderboard__rank${i < 3 ? ' leaderboard__rank--top' : ''}`}>
                      {i + 1}
                    </span>
                  </td>
                  <td>
                    <Link to={dest} className="leaderboard-link" aria-label={`${ep.podcast_name} — ${ep.title}`}>
                      <div className="leaderboard__episode-title">{ep.title}</div>
                      <div className="leaderboard__podcast-name">{ep.podcast_name}</div>
                    </Link>
                  </td>
                  <td>
                    <span className="leaderboard__stat">{section.format(episodeMetric(ep, section.key))}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default function Leaderboard() {
  // The prerenderer runs the same SECTIONS list through fetchTopEpisodes and
  // inlines the resulting rows, so the static HTML ships the full leaderboard
  // rather than five skeleton tables.
  const boot = pageData('leaderboard');

  useEffect(() => applyMeta(leaderboardMeta()), []);

  return (
    <div>
      <Link to="/podcasts" className="back-link" aria-label="Back to podcasts">
        &larr; All podcasts
      </Link>
      <h1 className="page-heading">Leaderboard</h1>
      <p className="page-subheading">Top episodes across all podcasts by stat category.</p>

      {SECTIONS.map(section => (
        <LeaderboardSection key={section.key} section={section} seed={boot?.sections?.[section.key]} />
      ))}
    </div>
  );
}

// Exported so scripts/prerender.js resolves exactly the sections this page renders.
export { SECTIONS };
